/**
 * Resolves configured action buttons against a record.
 *
 * Buttons are configured in admin rather than written into the template, so a
 * builder does not hand-write `<button>` tags and, more importantly, so the
 * destination of a button is stored config rather than markup a visitor could
 * influence.
 *
 * TWO URL RULES, BOTH LOAD-BEARING:
 *
 * 1. Interpolated values are `encodeURIComponent`-escaped. A record value
 *    containing `&` or `#` would otherwise forge extra query parameters —
 *    an id of `1&amount=0` silently becoming a second parameter on a payment
 *    link is exactly the bug this prevents. Encoding also means a record value
 *    can never supply the URL's *scheme*: it is always a parameter, never the
 *    start of the string.
 *
 * 2. The finished URL is checked against a scheme allowlist. Checking after
 *    interpolation rather than before is deliberate — the admin's template and
 *    the record's contribution both have to survive the check.
 *
 * `visible_when` fails CLOSED: a condition whose operator is not recognised
 * hides the button. A payment button shown because nobody understood the rule
 * is worse than one wrongly hidden.
 */

import { interpolate, resolvePath, hasPlaceholder } from './recordTemplate';
import { isSafeUrl } from './templateSanitiser';
import { pageHref } from './pageHref';

/**
 * Interpolates a URL template, escaping every substituted value.
 *
 * Done by resolving each placeholder individually rather than reusing
 * `interpolate`, because the escaping must apply to the VALUE only — escaping
 * the whole finished string would mangle the admin's own `?` and `&`.
 */
function interpolateUrl(template, scope) {
  return String(template).replace(
    /\{\{\s*([^}|]+?)\s*\}\}/g,
    (_match, path) => {
      const value = resolvePath(scope, path.trim());
      if (value === null || value === undefined) return '';
      return encodeURIComponent(String(value));
    },
  );
}

/**
 * Resolves one action to an href, or null when it does not navigate.
 *
 * Returns null for a `submit` action (it posts, it does not navigate) and for
 * any URL that fails the scheme allowlist.
 */
export function buildActionHref(action, scope) {
  if (!action || action.type !== 'redirect') return null;

  if (action.target === 'page') {
    // Page Builder pages are served under /app, which is what pageHref exists
    // to say. This branch built a root-level `/slug` and every such button
    // landed on the SPA's 404 — the one place left behind when navigation
    // moved to /app (resolveNavHref went with it; this did not).
    const slug = String(action.page_slug || '').trim().replace(/^\/+/, '');
    return slug ? pageHref(slug) : null;
  }

  const raw = String(action.url || '').trim();
  if (!raw) return null;

  const href = interpolateUrl(raw, scope);
  return isSafeUrl(href) ? href : null;
}

/* Exported for `allowed_when`, which the certificate download reads with the
 * SAME operator set — the server runs both conditions through one function for
 * exactly that reason, and a second implementation here would eventually
 * disagree with it about what `in` means. Note the two keys take OPPOSITE
 * defaults for a missing condition, so the caller supplies that part: see
 * certificateRules.isEligible. */
export function evaluateCondition(condition, scope, recordAlias) {
  // An array is several conditions that must ALL hold. The server answers a list
  // the same way, and that symmetry is the point: if only one side understood
  // it, a button visible here would be refused on submit — or worse, a hidden
  // one accepted.
  if (Array.isArray(condition)) {
    return condition.every(c => evaluateCondition(c, scope, recordAlias));
  }
  if (!condition || !condition.field) return true;      // no condition = always show

  // A condition may name the field either fully ("course.status") or bare
  // ("status"). The SERVER resolves it by taking the last segment and reading
  // that column off the record, so the client must accept the same two forms —
  // otherwise a rule that passes server-side silently hides the button here,
  // and the two disagree about whether an action is available.
  let value = resolvePath(scope, condition.field);
  if (value === undefined) {
    const leaf = String(condition.field).split('.').pop();
    if (recordAlias) value = resolvePath(scope, `${recordAlias}.${leaf}`);
    if (value === undefined) value = resolvePath(scope, leaf);
  }
  const target = condition.value;

  // Text comparison ignores case. A builder typing "open" against a column
  // holding "OPEN" would otherwise silently hide the button, with nothing on
  // screen to explain why — the commonest way to get this wrong. Non-strings
  // still compare exactly.
  const same = (a, b) =>
    (typeof a === 'string' && typeof b === 'string')
      ? a.toLowerCase() === b.toLowerCase()
      : a === b;

  switch (condition.op) {
    case 'eq':       return same(value, target);
    case 'neq':      return !same(value, target);
    case 'in':       return Array.isArray(target) && target.some(t => same(value, t));
    case 'not_in':   return Array.isArray(target) && !target.some(t => same(value, t));
    case 'is_set':   return value !== null && value !== undefined && value !== '';
    case 'is_blank': return value === null || value === undefined || value === '';
    default:
      // Fail closed — see module note.
      return false;
  }
}

/**
 * Resolves the buttons that should appear at `placement` ('detail' | 'card').
 *
 * Returns render-ready descriptors. A redirect button whose URL fails the
 * allowlist is DROPPED rather than rendered inert: a button that silently does
 * nothing is worse than no button, and one that navigates somewhere hostile is
 * worse still.
 */
export function resolveActionButtons(buttons, scope, placement, recordAlias) {
  if (!Array.isArray(buttons) || buttons.length === 0) return [];

  return buttons
    .filter(btn => btn && btn.id)
    .filter(btn => !btn.placement || btn.placement.includes(placement))
    .filter(btn => evaluateCondition(btn.visible_when, scope, recordAlias))
    .map(btn => {
      const type = btn.action?.type === 'submit' ? 'submit' : 'redirect';
      return {
        id: btn.id,
        type,
        label: hasPlaceholder(btn.label) ? interpolate(btn.label, scope) : (btn.label || ''),
        icon: btn.icon || '',
        style: btn.style || 'filled',
        href: buildActionHref(btn.action, scope),
        openInNewTab: Boolean(btn.action?.open_in_new_tab),
        action: btn.action || {},
        order: Number(btn.order) || 0,
      };
    })
    .filter(btn => btn.type === 'submit' || btn.href !== null)
    .sort((a, b) => a.order - b.order);
}
